import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { switchMap, filter, take, catchError } from 'rxjs/operators';

import { OAuthService } from '@app/auth/oauth.service';
import { AuthStatus, AuthState } from '@app/auth/auth.state';
import { env } from '@envs/env';

export interface Envelope<T> {
    success: boolean;
    code: number;
    message: string;
    http_code: number;
    http_message: string;
    detailed?: string | string[] | Array<{ field: string; message: string }>;
    data: Array<T>;
    count: number;
    pagination?: {
        next: string;
        prev: string;
    };
}

@Injectable({
    providedIn: 'root',
})
export class FlumeApiInterceptor implements HttpInterceptor {
    private absUrlTest = new RegExp('^(?:[a-z]+:)?//', 'i');

    private exceptions = ['/oauth/token'];

    constructor(private oauthService: OAuthService, private authState: AuthState) {}

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        req = this.addBaseUrl(req);

        // Add access token to the headers
        req = this.addAuthenticationToken(req);

        req = req.clone({
            headers: req.headers
                .set('Cache-Control', 'no-cache')
                .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
                .set('If-Modified-Since', '0'),
        });

        return next.handle(req).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.oauthService.authEvents$.pipe(
                        filter((state) => state === AuthStatus.AUTH_VALID),
                        take(1),
                        switchMap(() => {
                            req = this.addBaseUrl(req);
                            return next.handle(this.addAuthenticationToken(req));
                        })
                    );
                }
                if (error instanceof HttpErrorResponse && error.status === 403) {
                    this.oauthService.clearTokens();
                }
                return throwError(error);
            })
        );
    }

    private addBaseUrl(req: HttpRequest<any>): HttpRequest<any> {
        if (!this.absUrlTest.test(req.url)) {
            req = req.clone({ url: `${env.apiUrl}${req.url}` });
        }
        return req;
    }

    private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
        const token = this.oauthService.getAccessToken();
        if (!token) {
            return request;
        }

        if (!request.url.includes(env.apiUrl) || request.url.includes('/oauth/token')) {
            return request;
        }

        return request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + token),
        });
    }
}
